import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Clock, Star, Home, Shield } from 'lucide-react';
import Footer from '@/components/Footer';
import Navigation from '@/components/Navigation';
import FloatingActionButtons from '@/components/FloatingActionButtons';

const Hemstädning = () => {
  return (
    <>
      <Navigation />
      <Helmet>
        <title>Hemstädning - RenGlans Städtjänster</title>
        <meta name="description" content="Professionell hemstädning för ditt hem. Vi erbjuder noggrann och pålitlig städning med fokus på kvalitet och miljön." />
        <meta property="og:title" content="Hemstädning - RenGlans Städtjänster" />
        <meta property="og:description" content="Få ett skinande rent hem med vår professionella hemstädning. Vi anpassar oss efter dina behov och önskemål." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-32 pb-20 flex items-center justify-center overflow-hidden" style={{ minHeight: 'calc(80vh - 120px)' }}>
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/house-cleaners.webp)',
            filter: 'blur(4px)',
            transform: 'scale(1.02)',
            zIndex: 0,
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-[hsl(var(--hero-overlay))]/40" />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6"
          >
            Hemstädning
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-8"
          >
            Professionell städning som ger dig mer tid till det roliga i livet
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button 
              className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
              onClick={() => window.location.href = '/'}
            >
              Få Gratis Offert
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden translate-y-px">
          <svg 
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none"
            className="w-full h-12 md:h-16"
          >
            <path d="M0,60 C200,120 400,0 600,60 C800,120 1000,0 1200,60 V120 H0 V60 Z" fill="#FEF2F2" />
          </svg>
        </div>
      </section>

      {/* Om Hemstädning */}
      <section className="py-20 bg-red-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Professionell Hemstädning för Ditt Hem</h2>
            <div className="prose prose-lg text-gray-600 text-left mx-auto">
              <p className="mb-6">
                En ren och fräsch bostad är grunden för ett gott välmående. Vår professionella hemstädning är utformad för att ge dig ett skinande rent hem, oavsett om du behöver regelbunden städning, en grundlig genomgång eller hjälp inför speciella tillfällen.
              </p>
              <p className="mb-6">
                Med vår erfarenhet och noggrannhet tar vi hand om allt från grundläggande städ till mer krävande rengöringsarbeten. Vi använder miljövänliga rengöringsmedel som är effektiva men ändå skonsamma mot både miljön och dina inredningsdetaljer.
              </p>
              <p className="mb-6">
                Vårt mål är att göra din vardag lättare genom att ta hand om städningen åt dig. Du får mer tid att göra det du verkligen tycker om, medan vi ser till att ditt hem alltid känns fräscht och välkomnande.
              </p>
            </div>
          </div>
          
        </div>
      </section>

      {/* Red Wave Divider */}
      <div className="relative">
        <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ height: '80px', zIndex: 10 }}>
          <svg 
            viewBox="0 0 1200 80" 
            preserveAspectRatio="none"
            className="w-full h-full block"
          >
            <path d="M0,0C0,0,100,60,300,40S600,20,900,40s300,20,300,20v20H0V0z" className="fill-red-500" />
          </svg>
        </div>
      </div>

      {/* CTA Section */}
      <section className="relative py-20 bg-red-500 text-white overflow-hidden">
        <div className="absolute inset-0 transform translate-z-0" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Redo för ett skinande rent hem?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Få din kostnadsfria offert redan idag och upplev skillnaden med professionell städservice.
          </p>
          <div className="flex justify-center">
            <Button 
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-12 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/'}
            >
              Få Gratis Offert
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
      <FloatingActionButtons />
    </>
  );
};

export default Hemstädning;
